import { isOpen, type Homework } from '@adhd/shared';
import { useCallback, useEffect, useState } from 'react';
import { useOnResume } from '../lib/fresh.js';
import { Link } from 'react-router-dom';
import { ClipboardIcon } from './Icons.js';
import { api } from '../lib/api.js';

/**
 * Therapy homework: a question he was set, for a while.
 *
 * TWO PLACES, AND THE SPLIT IS THE DESIGN. Answering one is a daily act, so the
 * row sits on Today where he lands. Starting one happens about twice a year, so
 * it sits at the foot of "How today's going" beside marking something big —
 * inside a fold, where Today's surface does not change at all for it. The app
 * already made that choice for life events, which are the same shape of thing:
 * rare, spanning, his words. Settings even points there for them.
 *
 * It was in Settings first, and he could not find it. That is the only
 * usability test that counts.
 *
 * NO TICK, NO COUNT, NO STREAK. `DESIGN.md` §2.1 ("never punish a gap") and the
 * badge note at :476 ("a badge for a 30-day perfect streak is a loaded gun
 * pointed at day 31") both land on this. The row says "written today" once
 * there is something, and otherwise says nothing at all — never "not yet",
 * which is the same fact aimed at him as a failure.
 */

function useOpenHomework(): { open: Homework | null; today: string; reload: () => void } {
  const [open, setOpen] = useState<Homework | null>(null);
  const [today, setToday] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .homework()
      .then((r) => {
        if (cancelled) return;
        setToday(r.today);
        setOpen(r.items.find((h) => isOpen(h, r.today)) ?? null);
      })
      // Silent. This is an optional line on screens whose job is to be quick; a
      // banner about a failed fetch of something that may not exist would be
      // worse than the line not being there.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tick]);

  // The homework's last day may have passed while the app was away (20 Oct 2026).
  useOnResume(() => setTick((t) => t + 1));

  return { open, today, reload: useCallback(() => setTick((t) => t + 1), []) };
}

/**
 * The homework row on Today, and ONLY while one is running.
 *
 * Nothing otherwise: no empty state, no "start one" line, no dashed
 * placeholder. Today is the screen he opens every day and this is a feature he
 * uses about twice a year, so its footprint when idle is zero. His words, after
 * three attempts at putting it somewhere: "it feels like things are getting
 * cluttered, i dont want that — i want things clean".
 *
 * Starting one, and everything about past ones, lives in the Log — beside
 * "Print or save" and "Share with someone", which is the row of things you do
 * ABOUT your record rather than in it. The Log is a nav tab, so it is one tap
 * from anywhere without costing Today a pixel.
 */
export function KeepInMindRow(): JSX.Element | null {
  const { open } = useOpenHomework();
  if (!open) return null;

  /*
   * Built exactly like its neighbours: icon, a growing label, a short meta on
   * the right. It was a stacked `.option` first and read as a different species
   * of thing sitting in the middle of the list — the row has to be a peer of
   * "How are you doing?" or it looks misplaced, which is what it looked like.
   *
   * `grow` is what lets a therapist's question wrap to two lines without
   * shoving the date out of the box; it is the class the other rows already
   * use for the same reason.
   */
  return (
    <Link className="option option-row keep-in-mind" to={`/homework/${open.id}`}>
      <ClipboardIcon />
      <strong className="grow">{open.title}</strong>
      <span>{open.answeredToday ? 'Written' : `To ${formatUntil(open.endsOn)}`}</span>
    </Link>
  );
}

/** "6 Oct" — short, because it sits in lines that should read at a glance. */
export function formatUntil(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}
